import { useState, useEffect } from "react";
import { X, ChevronLeft, Plus, Edit2, Trash2 } from "lucide-react";

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

/**
 * Right slide-over panel for GST Invoice management at checkout.
 *
 * Props:
 *   open         bool
 *   onClose      fn()
 *   gstList      [{id,gstin,business_name}]
 *   selectedId   string | null
 *   onSave       fn(entry)          — entry has id for edit, no id for new
 *   onDelete     fn(id)
 *   onConfirm    fn(selectedEntry)  — closes panel, applies selection
 */
export default function GstDrawer({ open, onClose, gstList, selectedId, onSave, onDelete, onConfirm }) {
  const [view, setView]               = useState("list"); // "list" | "form"
  const [editTarget, setEditTarget]   = useState(null);   // null = new entry
  const [localSel, setLocalSel]       = useState(selectedId);
  const [form, setForm]               = useState({ gstin: "", business_name: "" });
  const [error, setError]             = useState("");

  // Reset each time panel opens
  useEffect(() => {
    if (open) {
      setView("list");
      setEditTarget(null);
      setForm({ gstin: "", business_name: "" });
      setError("");
      setLocalSel(selectedId);
    }
  }, [open, selectedId]);

  const openAdd = () => {
    setEditTarget(null);
    setForm({ gstin: "", business_name: "" });
    setError("");
    setView("form");
  };

  const openEdit = (entry) => {
    setEditTarget(entry);
    setForm({ gstin: entry.gstin, business_name: entry.business_name });
    setError("");
    setView("form");
  };

  const handleSave = () => {
    const gstin = form.gstin.toUpperCase().replace(/\s/g, "");
    if (!GSTIN_REGEX.test(gstin)) {
      setError("Enter a valid 15-digit GSTIN (e.g. 07AAGFF2194N1Z1)");
      return;
    }
    if (!form.business_name.trim()) {
      setError("Business name is required");
      return;
    }
    setError("");
    onSave(editTarget
      ? { ...editTarget, gstin, business_name: form.business_name.trim() }
      : { gstin, business_name: form.business_name.trim() }
    );
    setView("list");
  };

  const handleConfirm = () => {
    if (!localSel) return;
    const entry = gstList.find(g => g.id === localSel);
    if (entry) onConfirm(entry);
  };

  if (!open) return null;

  return (
    <>
      {/* Dark overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/40 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-in panel */}
      <div className="fixed top-0 right-0 z-50 h-full w-full sm:w-[420px] bg-white shadow-2xl flex flex-col">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          {view === "form" ? (
            <button
              onClick={() => { setView("list"); setError(""); }}
              className="flex items-center gap-1 text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {editTarget ? "Edit GST Details" : "Add New GST Details"}
            </button>
          ) : (
            <h2 className="text-base font-bold text-slate-900">Select GST Details</h2>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">

          {/* LIST VIEW */}
          {view === "list" && (
            <>
              {gstList.length === 0 && (
                <div className="px-5 py-10 text-center">
                  <p className="text-sm text-slate-500 font-medium">No GST details saved</p>
                  <p className="text-xs text-slate-400 mt-1">Add your GSTIN to receive a B2B invoice</p>
                </div>
              )}

              <div className="divide-y divide-slate-50">
                {gstList.map(entry => (
                  <div
                    key={entry.id}
                    onClick={() => setLocalSel(entry.id)}
                    className={`flex items-center gap-3 px-5 py-4 cursor-pointer transition-colors ${
                      localSel === entry.id ? "bg-blue-50" : "hover:bg-slate-50"
                    }`}
                  >
                    {/* Radio */}
                    <div
                      className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        localSel === entry.id ? "border-blue-600" : "border-slate-300"
                      }`}
                    >
                      {localSel === entry.id && (
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                      )}
                    </div>

                    {/* GSTIN + name */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 font-mono tracking-wider">{entry.gstin}</p>
                      <p className="text-sm text-slate-500 mt-0.5">{entry.business_name}</p>
                    </div>

                    {/* Edit */}
                    <button
                      onClick={e => { e.stopPropagation(); openEdit(entry); }}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-all flex-shrink-0"
                    >
                      <Edit2 className="w-3 h-3" /> Edit
                    </button>
                  </div>
                ))}
              </div>

              {/* Add new link */}
              <button
                onClick={openAdd}
                className="flex items-center gap-2 px-5 py-4 w-full text-left text-sm font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors border-t border-slate-100"
                data-testid="gst-add-new-btn"
              >
                <Plus className="w-4 h-4" />
                Add new GST Details
              </button>
            </>
          )}

          {/* FORM VIEW */}
          {view === "form" && (
            <div className="px-5 py-6 space-y-5">
              {/* GSTIN */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-semibold text-slate-700">GSTIN</label>
                  <span className="text-xs text-slate-400">{form.gstin.length}/15</span>
                </div>
                <input
                  type="text"
                  value={form.gstin}
                  onChange={e => {
                    setForm(f => ({ ...f, gstin: e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 15) }));
                    setError("");
                  }}
                  placeholder="e.g. 07AAGFF2194N1Z1"
                  maxLength={15}
                  data-testid="gst-gstin-input"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-mono tracking-wider focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              {/* Business Name */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-semibold text-slate-700">Business Name</label>
                  <span className="text-xs text-slate-400">{form.business_name.length}/60</span>
                </div>
                <input
                  type="text"
                  value={form.business_name}
                  onChange={e => setForm(f => ({ ...f, business_name: e.target.value.slice(0, 60) }))}
                  placeholder="Enter your registered business name"
                  maxLength={60}
                  data-testid="gst-business-name-input"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>

              {/* Info note */}
              <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-lg px-3.5 py-3">
                <span className="text-blue-500 text-sm flex-shrink-0 mt-0.5">ℹ️</span>
                <p className="text-xs text-blue-700 leading-relaxed">
                  To claim GST Input Tax Credit, enter your 15-digit GST Identification Number (GSTIN). A B2B tax invoice will be issued for this order.
                </p>
              </div>

              {error && (
                <p className="text-xs text-red-500 font-medium" data-testid="gst-error">{error}</p>
              )}

              {/* Delete button in edit mode */}
              {editTarget && (
                <button
                  onClick={() => { onDelete(editTarget.id); setView("list"); }}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-bold text-red-500 border border-red-200 rounded-xl hover:bg-red-50 transition-all"
                >
                  <Trash2 className="w-4 h-4" /> Remove GST Details
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Footer CTA ─────────────────────────────────────────────── */}
        <div className="px-5 py-4 border-t border-slate-100 bg-white">
          {view === "list" ? (
            <button
              onClick={handleConfirm}
              disabled={!localSel}
              data-testid="gst-confirm-btn"
              className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-all shadow-sm"
            >
              Confirm and Use
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={!form.gstin || !form.business_name}
              data-testid="gst-save-btn"
              className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-all shadow-sm"
            >
              Confirm and Save
            </button>
          )}
        </div>
      </div>
    </>
  );
}
